/**
 * Share page main program
 * Load models using sharer's token and restore scene
 */

// ========================================
// Cache 相關設定
// ========================================
const CACHE_VERSION = '1.0';
const CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 1 個月

interface CachedUserData {
  userId: string;
  models: any[];
  folders: any[];
  timestamp: number;
  version: string;
}

/**
 * 從 localStorage 讀取用戶數據 cache
 */
function loadUserDataFromCache(userId: string): CachedUserData | null {
  try {
    const cacheKey = `echov-user-cache-${userId}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const data: CachedUserData = JSON.parse(cached);
    
    // 檢查版本和過期時間
    if (data.version !== CACHE_VERSION) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    const now = Date.now();
    if (now - data.timestamp > CACHE_EXPIRY) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    // 延長 cache 時間（每次訪問時更新 timestamp）
    data.timestamp = now;
    localStorage.setItem(cacheKey, JSON.stringify(data));
    
    console.log(`✅ Loaded user data from cache (${data.models.length} models, ${data.folders.length} folders)`);
    return data;
  } catch (error) {
    console.warn('⚠️ Failed to load cache:', error);
    return null;
  }
}

/**
 * 將用戶數據保存到 localStorage cache
 */
function saveUserDataToCache(userId: string, models: any[], folders: any[]): void {
  try {
    const cacheKey = `echov-user-cache-${userId}`;
    const data: CachedUserData = {
      userId,
      models,
      folders,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    localStorage.setItem(cacheKey, JSON.stringify(data));
    console.log(`✅ Saved user data to cache (${models.length} models, ${folders.length} folders)`);
  } catch (error) {
    console.warn('⚠️ Failed to save cache:', error);
  }
}

// Get shareId from URL
const urlParams = new URLSearchParams(window.location.search);
const shareId = urlParams.get('id');

if (!shareId) {
  console.error('❌ Missing share ID');
  alert('Missing share ID');
  window.location.href = '/';
  throw new Error('Missing share ID');
}

// Dynamic API URL configuration
const currentHost = window.location.hostname;
const currentProtocol = window.location.protocol;

let API_URL: string;
if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
  API_URL = 'http://localhost:3002';
} else {
  API_URL = `${currentProtocol}//${currentHost}`;
}

// Get share data and set token before loading main_multi_model
(async () => {
  try {
    const response = await fetch(`${API_URL}/api/share/${shareId}`);
    
    // Check if response is OK
    if (!response.ok) {
      throw new Error(`Share not found or expired (${response.status})`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to load share');
    }
    
    const shareData = data.share;
    
    // Set sharer's token to sessionStorage (before loading main_multi_model)
    if (shareData.userToken) {
      sessionStorage.setItem('token', shareData.userToken);
      sessionStorage.setItem('user', JSON.stringify({
        userId: shareData.userId,
        username: shareData.username
      }));
      
      // 通知 main_multi_model 用最新的 sharer userId 重新 identify WebSocket
      // （share / iframe 訪客沒登入，localStorage.userInfo 不存在，
      //   要靠 sessionStorage.user 才能讓中央 WSS 把廣播推到這個頁面）
      const reidentify = (window as any).__echovReidentifyWS;
      if (typeof reidentify === 'function') {
        reidentify();
      }
      
      // 嘗試從 cache 加載用戶數據，如果沒有則會在 main_multi_model.ts 中從 API 獲取並保存
      const cachedData = loadUserDataFromCache(shareData.userId);
      if (cachedData) {
        console.log(`✅ Share page using cached data for user ${shareData.username}`);
      } else {
        console.log(`📡 Share page will fetch fresh data for user ${shareData.username}`);
      }
    } else {
      console.warn('⚠️ Share data missing userToken, using default models');
    }
    
    // Save share data to global variable
    (window as any).shareData = shareData;
    
    // 創建 visible 狀態映射，供 main_multi_model.ts 使用
    if (shareData.sceneState && shareData.sceneState.models) {
      const visibilityMap = new Map();
      shareData.sceneState.models.forEach((model: any) => {
        visibilityMap.set(model.id, model.visible !== false);
      });
      (window as any).shareVisibilityMap = visibilityMap;
      console.log(`📋 Share visibility map created with ${visibilityMap.size} models`);
    }
    
    // 設定 Model Markers 可見性狀態（在 markers 創建之前）
    if (shareData.sceneState && shareData.sceneState.modelMarkersVisible !== undefined) {
      const shouldHide = !shareData.sceneState.modelMarkersVisible;
      (window as any).modelMarkersHidden = shouldHide;
      console.log(`📋 Share model markers visibility set: ${shouldHide ? 'hidden' : 'visible'}`);
    }
    
  } catch (error) {
    console.error('❌ Failed to get share data:', error);
    alert('Failed to load share: ' + (error as Error).message);
    // Redirect to home page
    window.location.href = '/';
    throw error; // Stop execution
  }
})();

// Now load main program (it will use the token we just set)
import './main_multi_model';

// Wait for main program initialization, then restore scene state
window.addEventListener('load', async () => {
  // 隱藏初始的 loading overlay，顯示 loading bar
  const loadingOverlay = document.querySelector('.loading-overlay') as HTMLElement;
  if (loadingOverlay) {
    loadingOverlay.style.display = 'none';
  }
  
  // Wait for share data to load
  let attempts = 0;
  while (attempts < 50 && !(window as any).shareData) {
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }
  
  const shareData = (window as any).shareData;
  if (!shareData) {
    console.error('❌ Share data not loaded');
    return;
  }
  
  try {
    // Wait for main program initialization
    let attempts = 0;
    const maxAttempts = 150; // Max wait 30 seconds
    
    let map: any = null;
    let modelManager: any = null;
    let preJumped = false;

    while (attempts < maxAttempts) {
      map = (window as any).map;
      modelManager = (window as any).EchoV?.modelManager;

      // 一拿到 map 就馬上 jumpTo 到 share 鏡頭，不要等 modelManager / models 載完。
      // 否則使用者會先看到 main_multi_model.ts 設的預設地圖中心（通常離 share
      // 位置很遠），等 2~10 秒 models 載完才瞬移過去 — 視覺體驗仍像 zoom-in。
      // 先 jumpTo 後，整個 models 載入過程都已經在對的視角發生。
      if (map && !preJumped && shareData?.sceneState?.camera) {
        try {
          map.jumpTo({
            center: [shareData.sceneState.camera.lng, shareData.sceneState.camera.lat],
            zoom: shareData.sceneState.camera.zoom,
            pitch: shareData.sceneState.camera.pitch,
            bearing: shareData.sceneState.camera.bearing,
          });
          preJumped = true;
          console.log('🎯 [Share] Pre-jumped map camera (no zoom-in animation)');
        } catch (err) {
          console.warn('[Share] Pre-jump camera failed:', err);
        }
      }

      if (map && modelManager) {
        break;
      }

      await new Promise(resolve => setTimeout(resolve, 200));
      attempts++;
    }

    if (!map) {
      console.error('❌ Map not initialized');
      throw new Error('Map initialization failed');
    }

    if (!modelManager) {
      console.error('❌ ModelManager not initialized');
      throw new Error('ModelManager initialization failed');
    }
    
    // Wait for models to load
    attempts = 0;
    while (attempts < maxAttempts) {
      try {
        const allModels = modelManager.getAllModels ? modelManager.getAllModels() : modelManager.models;
        if (allModels && allModels.size > 0) {
          break;
        }
      } catch (error) {
        // Ignore errors, continue waiting
      }
      
      await new Promise(resolve => setTimeout(resolve, 200));
      attempts++;
    }
    
    // Restore camera position INSTANTLY.
    // 之前用 flyTo({ duration: 2000 }) 會做 2 秒 cinematic zoom 動畫，
    // 但使用者要求 share 一打開鏡頭就在指定位置，不要 zoom-into 動作。
    // jumpTo() 是 Mapbox 的瞬移版本，立即套用 center / zoom / pitch / bearing。
    map.jumpTo({
      center: [shareData.sceneState.camera.lng, shareData.sceneState.camera.lat],
      zoom: shareData.sceneState.camera.zoom,
      pitch: shareData.sceneState.camera.pitch,
      bearing: shareData.sceneState.camera.bearing,
    });
    
    // Restore Mapbox style (if different)
    if (shareData.sceneState.mapboxStyle && shareData.sceneState.mapboxStyle !== map.getStyle()?.name) {
      map.setStyle(shareData.sceneState.mapboxStyle);
      // setStyle 會把 projection 重置回預設（v3 預設是 globe），
      // 但 Custom Layer (Three.js frag model) 不支援 globe → 會錯位／消失。
      // 等新 style 載完後強制設回 mercator，與 main_multi_model.ts 初始化一致。
      map.once('style.load', () => {
        try { map.setProjection({ name: 'mercator' }); } catch (e) { /* ignore */ }
      });
    }
    
    // Restore model visibility and color
    if (shareData.sceneState.models && Array.isArray(shareData.sceneState.models)) {
      // Wait for models to fully load
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Get currently loaded model IDs
      const loadedModelIds = new Set<string>();
      if (modelManager && modelManager.getAllModels) {
        modelManager.getAllModels().forEach((_: any, modelId: string) => {
          loadedModelIds.add(modelId);
        });
      }
      
      console.log(`📋 Applying visibility state for ${shareData.sceneState.models.length} models`);
      
      for (const modelState of shareData.sceneState.models) {
        const modelId = modelState.id;
        
        // Only process loaded models, skip non-existent ones
        if (!loadedModelIds.has(modelId)) {
          console.log(`⚠️ Model ${modelId} not loaded, skipping`);
          continue;
        }
        
        try {
          // Set visibility
          if (modelState.visible !== undefined) {
            console.log(`👁️ Setting model ${modelId} visibility to ${modelState.visible}`);
            if (modelState.visible) {
              if (modelManager.showModel) {
                modelManager.showModel(modelId);
              }
            } else {
              if (modelManager.hideModel) {
                modelManager.hideModel(modelId);
              }
            }
          }
          
          // Set color
          if (modelState.color) {
            const colorController = (window as any).EchoV?.colorController;
            if (colorController && colorController.setModelColor) {
              colorController.setModelColor(modelId, modelState.color);
            }
          }
        } catch (error) {
          // Silently handle errors
        }
      }
    }
    
    // Restore building visibility
    if (shareData.sceneState.buildingsVisible !== undefined) {
      const buildingsHidden = !shareData.sceneState.buildingsVisible;
      
      // Update global variable
      (window as any).buildingsHidden = buildingsHidden;
      
      // Toggle building layers
      const style = map.getStyle();
      if (style && style.layers) {
        style.layers.forEach((layer: any) => {
          if (layer.id.toLowerCase().includes('building')) {
            if (map.getLayer(layer.id)) {
              map.setLayoutProperty(
                layer.id,
                'visibility',
                buildingsHidden ? 'none' : 'visible'
              );
            }
          }
        });
      }
      
      if (map.getLayer('3d-buildings')) {
        map.setLayoutProperty(
          '3d-buildings',
          'visibility',
          buildingsHidden ? 'none' : 'visible'
        );
      }
    }
    
    // Restore Functional Markers visibility
    if (shareData.sceneState.functionalMarkersVisible !== undefined) {
      const shouldHide = !shareData.sceneState.functionalMarkersVisible;
      
      // Update global variable
      (window as any).functionalMarkersHidden = shouldHide;
      
      // Wait for functional markers to be created
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Toggle all functional markers (directly find functional-marker-icon and functional-marker-label)
      const functionalIcons = document.querySelectorAll('.functional-marker-icon');
      functionalIcons.forEach((icon) => {
        const markerContainer = (icon as HTMLElement).closest('.mapboxgl-marker') as HTMLElement;
        if (markerContainer) {
          markerContainer.style.display = shouldHide ? 'none' : 'block';
        }
      });
      
      const functionalLabels = document.querySelectorAll('.functional-marker-label');
      functionalLabels.forEach((label) => {
        const markerContainer = (label as HTMLElement).closest('.mapboxgl-marker') as HTMLElement;
        if (markerContainer) {
          markerContainer.style.display = shouldHide ? 'none' : 'block';
        }
      });
      
      // Update button text
      const textSpan = document.getElementById('functionalMarkerToggleText');
      if (textSpan) {
        textSpan.textContent = shouldHide ? 'Functional Markers: OFF' : 'Functional Markers';
      }
    }
    
    // Model Markers visibility 已經在 markers 創建時設定好了
    // 只需要更新按鈕文字
    if (shareData.sceneState.modelMarkersVisible !== undefined) {
      const shouldHide = !shareData.sceneState.modelMarkersVisible;
      const textSpan = document.getElementById('modelMarkerToggleText');
      if (textSpan) {
        textSpan.textContent = shouldHide ? 'Model Markers: OFF' : 'Model Markers';
      }
      const btn = document.getElementById('modelMarkerToggleBtn');
      if (btn) {
        if (shouldHide) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      }
    }
    
    // Restore Marker Zoom toggle
    //
    // markerZoomEnabled 控制按 functional marker 時係咪先 flyTo 再開
    // popup。Share view 入面用家睇唔到 toolbar，無法自己改 — 所以分享者
    // Update Scene 時揀邊個狀態，share view 必須 honor。
    // applyMarkerZoomEnabled 已由 main_multi_model.ts expose 到 window
    // (見該檔案 single-source-of-truth helper)，這裡單純照樣套用。
    if (shareData.sceneState.markerZoomEnabled !== undefined) {
      const fn = (window as any).applyMarkerZoomEnabled;
      if (typeof fn === 'function') {
        try {
          fn(!!shareData.sceneState.markerZoomEnabled);
        } catch (err) {
          console.warn('[share] applyMarkerZoomEnabled threw', err);
        }
      }
    }

    // Restore RED QUEEN MODE
    if (shareData.sceneState.redQueenMode !== undefined && shareData.sceneState.redQueenMode === true) {
      // Wait to ensure models are loaded
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Set transparency
      if (modelManager && modelManager.setAllModelsTransparency) {
        await modelManager.setAllModelsTransparency(0.2);
      }
      
      // Update global state
      (window as any).isRedQueenMode = true;
      (window as any).isTransparent = true;
      
      // Update button text (if exists)
      const transparencyText = document.getElementById('transparencyText');
      if (transparencyText) {
        transparencyText.textContent = 'RED QUEEN MODE: ON';
      }
    }
    
    // 隱藏載入動畫
    const loadingOverlay = document.querySelector('.loading-overlay') as HTMLElement;
    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
    }
    
    // 在頁面上顯示分享信息
    const shareInfo = document.createElement('div');
    shareInfo.style.cssText = `
      position: fixed;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: #44d3de;
      padding: 10px 20px;
      border-radius: 8px;
      border: 1px solid #44d3de;
      font-size: 14px;
      z-index: 10000;
      pointer-events: none;
    `;
    shareInfo.textContent = `📤 ${shareData.username}'s shared scene (Views: ${shareData.views})`;
    document.body.appendChild(shareInfo);
    
    // Auto hide after 3 seconds
    setTimeout(() => {
      shareInfo.style.opacity = '0';
      shareInfo.style.transition = 'opacity 0.5s';
      setTimeout(() => shareInfo.remove(), 500);
    }, 3000);
    
  } catch (error) {
    console.error('❌ Failed to restore scene:', error);
    alert('Failed to restore scene: ' + (error as Error).message);
    
    // Hide loading animation
    const loadingOverlay = document.querySelector('.loading-overlay') as HTMLElement;
    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
    }
  }
});
